import Cookies from 'js-cookie';
import compact from 'lodash/compact';

import * as env from 'src/utils/lib/env';

const WRITER_EXT_ID_COOKIE_NAME = 'keywee_ext_id';
const APPLY_EXT_ID_COOKIE_NAME = 'apply_ext_id';

// Get 'chrome' browser object from global and check if it supports messaging
const { chrome } = window;
const doesSupportMessages =
  chrome && chrome.runtime && typeof chrome.runtime.sendMessage === 'function';

/**
 * Prepare list of possible extension IDs
 * @returns {string[]}
 */
const getExtensionIDs = () => {
  const result = [];

  // IDs from environment variable
  const fromEnv = env.getAuthorCompleteExtensionID();
  if (fromEnv) {
    const external = fromEnv.split(',');
    result.push(...external);
  }

  // ID from URL query
  const fromQuery = new URL(window.location.href).searchParams.get('extId');
  if (fromQuery) {
    result.push(fromQuery);
  }

  // Writer extension ID from cookie
  const writerCookie = Cookies.get(WRITER_EXT_ID_COOKIE_NAME);
  if (writerCookie) {
    result.push(writerCookie);
  }

  // Apply extension ID from cookie
  const applyCookie = Cookies.get(APPLY_EXT_ID_COOKIE_NAME);
  if (applyCookie) {
    result.push(applyCookie);
  }

  return result;
};

const sendMessageById = (extensionId, type, params) =>
  new Promise((resolve, reject) => {
    const message = { type, ...params };

    try {
      chrome.runtime.sendMessage(extensionId, message, (reply) => {
        if (chrome.runtime.lastError) {
          reject(chrome.runtime.lastError.message);
          return;
        }

        if (reply && reply.success) {
          resolve(reply.data);
          return;
        }

        reject(reply && reply.error);
      });
    } catch (error) {
      reject(error);
    }
  });

/**
 * Current extension ID would be stored here
 * @type {string|null}
 */
let currentExtensionId = null;

const checkExtensionId = (id) =>
  sendMessageById(id, 'external.ping')
    .then(() => id)
    .catch(() => null);

/**
 * Detect working extension to talk to
 * @returns {Promise}
 */
export async function detectExtensionId() {
  // If we already detected working ID - use it
  if (currentExtensionId) {
    return Promise.resolve(currentExtensionId);
  }

  // Possible IDs
  const ids = getExtensionIDs();

  // Check every ID
  const promises = ids.map((id) => checkExtensionId(id));

  // Use first valid ID, store it for future use
  return Promise.all(promises).then((results) => {
    [currentExtensionId] = compact(results);
    return currentExtensionId;
  });
}

/**
 * Send message to current working extension
 * @param type
 * @param params
 * @returns {Promise}
 */
export function sendMessage(type, params) {
  if (!doesSupportMessages) {
    return Promise.reject(new Error('Extension messages are not supported'));
  }

  // Get working extension ID
  return detectExtensionId().then((extensionId) => {
    if (!extensionId) {
      throw new Error('Cannot determine extension ID');
    }

    // Send message
    return sendMessageById(extensionId, type, params);
  });
}
